import React, { createContext, useState, ReactNode, useEffect, useRef } from "react";
import { UserProfile } from "../lib/types";
import { config } from "../Environment";

type AuthDataType = {
  email: string | undefined;
  accessToken: string | undefined;
  impersonatedBy: string | undefined;
};

interface AuthContextType {
  auth: AuthDataType;
  setAuth: React.Dispatch<React.SetStateAction<AuthDataType>>;
  user: UserProfile;
  setUser: React.Dispatch<React.SetStateAction<UserProfile>>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [auth, setAuth] = useState<AuthDataType>({
    email: undefined,
    accessToken: undefined,
    impersonatedBy: undefined,
  });
  const [user, setUser] = useState<UserProfile>({});

  // Initialize auth and user from localStorage on mount
  useEffect(() => {
    try {
      console.log("[AuthProvider] Initializing from localStorage...");

      // Read auth from localStorage
      const storedAuthJSON = localStorage.getItem("auth");
      if (storedAuthJSON && storedAuthJSON !== "{}") {
        const storedAuth = JSON.parse(storedAuthJSON);
        if (storedAuth?.email && storedAuth?.accessToken) {
          console.log(`[AuthProvider] Found stored auth for: ${storedAuth.email}`);
          setAuth(storedAuth);
        } else {
          console.log("[AuthProvider] Stored auth is incomplete");
        }
      } else {
        console.log("[AuthProvider] No stored auth found");
      }

      // Read user from localStorage
      const storedUserJSON = localStorage.getItem("user");
      if (storedUserJSON && storedUserJSON !== "{}") {
        const storedUser = JSON.parse(storedUserJSON);
        if (storedUser?.email) {
          console.log(`[AuthProvider] Found stored user: ${storedUser.email}`);
          setUser(storedUser);
        }
      }
    } catch (error) {
      console.error("[AuthProvider] Error loading from localStorage:", error);
      // On error, keep defaults (logged out state)
    } finally {
      // Mark as done loading so RequireAuth can check
      setIsLoading(false);
      console.log("[AuthProvider] Initialization complete");
    }
  }, []);

  // Save auth to localStorage when it changes
  useEffect(() => {
    if (!isLoading) {
      try {
        // Log when auth is being cleared (potential unexpected logout)
        if (!auth?.email && !auth?.accessToken) {
          const storedAuth = localStorage.getItem("auth");
          if (storedAuth && storedAuth !== "{}" && storedAuth !== '{"email":undefined,"accessToken":undefined,"impersonatedBy":undefined}') {
            console.warn("[AuthProvider] Auth cleared — user logged out", new Error().stack);
          }
        }
        localStorage.setItem("auth", JSON.stringify(auth));
        console.log(`[AuthProvider] Saved auth to localStorage: ${auth?.email || 'none'}`);
      } catch (error) {
        console.error("[AuthProvider] Error saving auth to localStorage:", error);
      }
    }
  }, [auth, isLoading]);

  // Save user to localStorage when it changes
  useEffect(() => {
    if (!isLoading) {
      try {
        localStorage.setItem("user", JSON.stringify(user));
        console.log(`[AuthProvider] Saved user to localStorage`);
      } catch (error) {
        console.error("[AuthProvider] Error saving user to localStorage:", error);
      }
    }
  }, [user, isLoading]);

  // Listen for user-profile-updated custom event (dispatched by SocketProvider
  // when the backend sends user:profile_updated via socket)
  useEffect(() => {
    const handleProfileUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      const profileData = customEvent.detail;
      if (profileData) {
        console.log("[AuthProvider] Received user-profile-updated event, updating user");
        setUser(profileData);
      }
    };

    window.addEventListener("user-profile-updated", handleProfileUpdated);
    return () => {
      window.removeEventListener("user-profile-updated", handleProfileUpdated);
    };
  }, []);

  // Re-fetch user profile when tab becomes visible after being hidden for 5+ minutes
  // This ensures roles and other profile data stay fresh without requiring re-login
  const lastHiddenTimeRef = useRef<number>(0);
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        lastHiddenTimeRef.current = Date.now();
        return;
      }

      // Tab became visible — check if hidden for 5+ minutes
      const hiddenDuration = Date.now() - lastHiddenTimeRef.current;
      if (hiddenDuration < 5 * 60 * 1000) return;

      // Only refresh if user is authenticated
      const storedAuthJson = localStorage.getItem("auth") ?? "{}";
      const storedAuth = JSON.parse(storedAuthJson);
      if (!storedAuth?.accessToken) return;

      try {
        console.log("[AuthProvider] Tab visible after 5+ min, refreshing user profile");
        const response = await fetch(config.API_BASE + "/api/user/profile", {
          method: "GET",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${storedAuth.accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.item) {
            setUser(result.item);
            console.log("[AuthProvider] User profile refreshed on visibility change");
          }
        }
      } catch (e) {
        console.warn("[AuthProvider] Failed to refresh profile on visibility change:", e);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ auth, setAuth, user, setUser, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
